// =========================================================================
// AlertRoutingPanel — root cause and routing on the alert page.
// Issues module, fork-owned (zone C).
//
// Answers reviewer feedback (Paul Telford, 25 Sep 2026): some observations
// are how the machine is driven and belong with the Operating Supervisor,
// Superintendent and Mine Control; others are the equipment and belong with
// the Reliability Engineer and Maintenance Planner. The panel shows the
// verdict, the two signals it rests on side by side, the day / night split,
// the evidence, and one card per person the alert is pushed to.
//
// UI layer. The engine calls (`rootCause`, `routeFinding`,
// `machineOperatorShares`) run once per alert over the session's reliability
// context; every projection comes from the sibling transform.
//
// @see docs/logic/issues/alert-routing.md
// @see docs/engines/reliability.md
// =========================================================================

import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { HelpAnchor, Kicker, SkeletonBlock } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import {
  machineOperatorShares,
  rootCause,
  routeFinding,
} from '@/versions/v1/shared/reliability';
import { useReliability } from '@/versions/v1/shared/reliability/useReliability';
import type { Alert } from '@/versions/v1/shared/seed/alerts';
import { StackedBar } from '@/versions/v1/shared/viz/StackedBar';
import {
  chanceLabel,
  equipmentView,
  operatorChart,
  shiftSplit,
  signalMeter,
  verdictLine,
  type SignalMeter,
} from './AlertRoutingPanel.transform';
import { CausePill, RosterNote, RouteCards, useRouteSends } from './RouteCards';
import { CAUSE_META, pct, sideCounts } from './RouteCards.transform';

export function AlertRoutingPanel({ alert }: { alert: Alert }) {
  const { ctx, loading, error } = useReliability();
  const sends = useRouteSends();

  const view = useMemo(() => {
    if (!ctx) return null;
    const finding = rootCause(alert.machine, alert.comp, ctx);
    const decision = routeFinding(finding, ctx, alert);
    const shares = machineOperatorShares(ctx.attribution, alert.machine, { component: alert.comp });
    return {
      finding,
      decision,
      chart: operatorChart(shares, finding.topOperator?.operatorId ?? null),
      equipment: equipmentView(alert.machine, alert.comp, ctx.dailyMetric),
      shifts: shiftSplit(ctx.attribution, alert.machine, alert.comp),
      sides: sideCounts(decision),
    };
  }, [ctx, alert]);

  return (
    <Commentable name="AlertRouting">
      <section
        aria-labelledby="alert-routing-title"
        className="rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] p-4"
      >
        <header className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <Kicker className="block">Root cause &amp; routing</Kicker>
            <h2 id="alert-routing-title" className="mt-1 text-base font-semibold text-[var(--ink-primary)]">
              Whose problem is it, and who acts
            </h2>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <Link
              to="/issues/routing"
              className="inline-flex items-center gap-1 text-[11.5px] font-medium text-[var(--accent)] hover:underline"
            >
              All routing &amp; training <ArrowRight aria-hidden className="h-3.5 w-3.5" />
            </Link>
            <HelpAnchor doc="issues/alert-routing" />
          </div>
        </header>

        {error ? (
          <p role="alert" className="rounded-lg border border-[var(--hairline)] bg-[var(--surface-2)] p-3 text-xs text-[var(--ink-secondary)]">
            The shift and event data behind the root-cause call did not load ({error.message}). The alert above
            stands on its own; reload to retry.
          </p>
        ) : loading || !view ? (
          <div className="space-y-3" aria-busy>
            <SkeletonBlock className="h-[64px] rounded-lg" />
            <div className="grid gap-3 lg:grid-cols-2">
              <SkeletonBlock className="h-[210px] rounded-lg" />
              <SkeletonBlock className="h-[210px] rounded-lg" />
            </div>
            <SkeletonBlock className="h-[180px] rounded-lg" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Verdict */}
            <Commentable name="RoutingVerdict">
              <div
                className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-lg border border-[var(--hairline)] p-3"
                style={{
                  background: `linear-gradient(100deg, ${CAUSE_META[view.finding.cause].soft}, var(--surface-1) 70%)`,
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="mono text-[28px] font-semibold leading-none" style={{ color: CAUSE_META[view.finding.cause].color }}>
                    {pct(view.finding.confidence)}
                  </div>
                  <div className="grid gap-1">
                    <CausePill cause={view.finding.cause} long size="md" />
                    <span className="text-[10.5px] text-[var(--ink-tertiary)]">confidence in the call</span>
                  </div>
                </div>
                <p className="min-w-[16rem] flex-1 text-[12.5px] leading-relaxed text-[var(--ink-secondary)]">
                  {verdictLine(view.finding)}
                </p>
                <div className="flex shrink-0 flex-col items-end gap-0.5 text-[11px] text-[var(--ink-tertiary)]">
                  <span>
                    Routed to <b className="font-semibold text-[var(--ink-primary)]">{view.decision.routes.length}</b>{' '}
                    {view.decision.routes.length === 1 ? 'person' : 'people'}
                  </span>
                  <span>
                    {view.sides.operations} operations · {view.sides.maintenance} maintenance
                  </span>
                </div>
              </div>
            </Commentable>

            {/* Signals */}
            <Commentable name="RoutingSignals">
              <div className="grid gap-3 lg:grid-cols-2">
                <SignalCard
                  title="Operator signal"
                  subtitle={`${alert.comp} events on ${alert.machine}, by who was driving`}
                  meter={signalMeter(view.finding.operatorSignal)}
                  tone="var(--info)"
                >
                  {view.chart.totalEvents === 0 ? (
                    <p className="py-4 text-[11.5px] text-[var(--ink-tertiary)]">
                      No operator-sensitive events on this component in the window — the operator signal has
                      nothing to weigh.
                    </p>
                  ) : (
                    <>
                      <StackedBar
                        rows={view.chart.rows}
                        categories={view.chart.categories}
                        colors={view.chart.colors}
                        format={(v) => `${Math.round(v)}%`}
                        axisFormat={(v) => (v === 0 ? '' : `${Math.round(v)}%`)}
                        gutter={104}
                        rowHeight={28}
                        viewWidth={520}
                        title={`Share of ${alert.machine}'s hours against share of its ${alert.comp.toLowerCase()} events, by operator`}
                      />
                      <table className="mt-1 w-full text-[11px]">
                        <thead>
                          <tr className="text-left text-[10px] uppercase tracking-wide text-[var(--ink-tertiary)]">
                            <th className="py-1 font-medium">Operator</th>
                            <th className="py-1 text-right font-medium">Hours</th>
                            <th className="py-1 text-right font-medium">Events</th>
                          </tr>
                        </thead>
                        <tbody>
                          {view.chart.lines.map((l) => (
                            <tr key={l.name} className="border-t border-[var(--hairline)]">
                              <td className="py-1">
                                <span className="inline-flex items-center gap-1.5">
                                  <i aria-hidden className="inline-block h-2 w-2 rounded-[2px]" style={{ background: l.color }} />
                                  <span className={cn(l.top ? 'font-semibold text-[var(--ink-primary)]' : 'text-[var(--ink-secondary)]')}>
                                    {l.name}
                                  </span>
                                  {l.crew ? <span className="text-[var(--ink-tertiary)]">crew {l.crew}</span> : null}
                                </span>
                              </td>
                              <td className="mono py-1 text-right text-[var(--ink-secondary)]">
                                {l.hours} h <span className="text-[var(--ink-tertiary)]">· {Math.round(l.hoursPct)}%</span>
                              </td>
                              <td className="mono py-1 text-right text-[var(--ink-primary)]">
                                {l.events} <span className="text-[var(--ink-tertiary)]">· {Math.round(l.eventsPct)}%</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="mt-2 text-[10.5px] text-[var(--ink-tertiary)]">
                        {view.chart.totalEvents} operator-sensitive events
                        {chanceLabel(view.finding.pValue) ? (
                          <>
                            {' · '}chance of this concentration if events followed hours:{' '}
                            <span className="mono text-[var(--ink-secondary)]">{chanceLabel(view.finding.pValue)}</span>
                          </>
                        ) : null}
                      </p>
                    </>
                  )}
                </SignalCard>

                <SignalCard
                  title="Equipment signal"
                  subtitle={`${alert.comp} condition and drift`}
                  meter={signalMeter(view.finding.equipmentSignal)}
                  tone="var(--warn)"
                >
                  <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
                    <div className="mono text-[28px] font-semibold leading-none">
                      <span style={{ color: view.equipment.bandColor ?? 'var(--ink-primary)' }}>
                        {view.equipment.score ?? '—'}
                      </span>
                      <span className="text-base font-normal text-[var(--ink-tertiary)]">/100</span>
                    </div>
                    {view.equipment.bandLabel ? (
                      <span
                        className="mb-0.5 rounded-full px-2 py-0.5 text-[11px] font-medium"
                        style={{
                          color: view.equipment.bandColor ?? undefined,
                          background: 'var(--surface-2)',
                        }}
                      >
                        {view.equipment.bandLabel}
                      </span>
                    ) : null}
                  </div>
                  {view.equipment.note ? (
                    <p className="mt-2 text-[11.5px] italic leading-relaxed text-[var(--ink-secondary)]">
                      &ldquo;{view.equipment.note}&rdquo;
                    </p>
                  ) : null}
                  <dl className="mt-3 grid gap-1.5 border-t border-[var(--hairline)] pt-2 text-[11px]">
                    {view.equipment.drift ? (
                      <div className="flex flex-wrap justify-between gap-2">
                        <dt className="text-[var(--ink-tertiary)]">{view.equipment.drift.metricName}</dt>
                        <dd className="mono text-[var(--ink-primary)]">
                          {view.equipment.drift.first} → {view.equipment.drift.last} {view.equipment.drift.unit}
                          <span
                            className="ml-2"
                            style={{ color: view.equipment.drift.drifting ? 'var(--crit)' : 'var(--ink-tertiary)' }}
                          >
                            {view.equipment.drift.pctPerDay > 0 ? '+' : ''}
                            {view.equipment.drift.pctPerDay.toFixed(1)}%/day
                          </span>
                        </dd>
                      </div>
                    ) : (
                      <div className="text-[var(--ink-tertiary)]">No tracked metric on this component — condition score only.</div>
                    )}
                    <div className="flex flex-wrap justify-between gap-2">
                      <dt className="text-[var(--ink-tertiary)]">Rule</dt>
                      <dd className="text-right text-[var(--ink-secondary)]">
                        condition &lt; 60 or drift &gt; 0.4 %/day on the component&rsquo;s own metric
                      </dd>
                    </div>
                  </dl>
                </SignalCard>
              </div>
            </Commentable>

            {/* Shift split + evidence */}
            <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.45fr)]">
              <div className="rounded-lg border border-[var(--hairline)] p-3">
                <Kicker className="mb-2 block">Day shift against night shift</Kicker>
                <div className="space-y-3">
                  {view.shifts.map((s) => (
                    <div key={s.shift}>
                      <div className="mb-1 flex items-baseline justify-between gap-2 text-[11.5px]">
                        <span className="font-medium text-[var(--ink-primary)]">{s.label}</span>
                        <span className="mono text-[var(--ink-tertiary)]">
                          {s.events} events · {s.hours} h ·{' '}
                          <span className="text-[var(--ink-primary)]">{s.ratePer100h}</span>/100 h
                        </span>
                      </div>
                      <SplitBar label="hours" value={s.hoursPct} color="var(--ink-tertiary)" />
                      <SplitBar label="events" value={s.eventsPct} color={s.eventsPct > s.hoursPct + 15 ? 'var(--info)' : 'var(--band-good)'} />
                    </div>
                  ))}
                </div>
                <Link
                  to={`/machines/${alert.machine}?tab=operators`}
                  className="mt-3 inline-block text-[11px] font-medium text-[var(--accent)] hover:underline"
                >
                  {alert.machine} shift by shift ›
                </Link>
              </div>

              <div className="rounded-lg border border-[var(--hairline)] p-3">
                <Kicker className="mb-2 block">Evidence, strongest first</Kicker>
                <ol className="space-y-1.5">
                  {view.decision.evidence.map((line, i) => (
                    <li key={line} className="flex gap-2 text-[11.5px] leading-relaxed text-[var(--ink-secondary)]">
                      <span className="mono mt-px w-4 flex-none text-right text-[10.5px] text-[var(--ink-tertiary)]">{i + 1}</span>
                      <span className="min-w-0">{line}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Routes */}
            <Commentable name="RoutingRoutes">
              <div>
                <Kicker className="mb-2 block">Pushed to</Kicker>
                <RouteCards decision={view.decision} sends={sends} />
              </div>
            </Commentable>

            <RosterNote machine={alert.machine} className="border-t border-[var(--hairline)] pt-3" />
          </div>
        )}
      </section>
    </Commentable>
  );
}

function SignalCard({
  title,
  subtitle,
  meter,
  tone,
  children,
}: {
  title: string;
  subtitle: string;
  meter: SignalMeter;
  tone: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0 rounded-lg border border-[var(--hairline)] p-3">
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[12.5px] font-semibold text-[var(--ink-primary)]">{title}</div>
          <div className="text-[11px] text-[var(--ink-tertiary)]">{subtitle}</div>
        </div>
        <div className="w-[8.5rem] shrink-0">
          <div className="relative h-1.5 rounded-full bg-[var(--surface-3)]">
            <i
              className="absolute inset-y-0 left-0 rounded-full"
              style={{ width: `${meter.pct}%`, background: meter.calls ? tone : 'var(--ink-tertiary)' }}
            />
            <i
              aria-hidden
              className="absolute -top-1 bottom-[-4px] w-px bg-[var(--ink-secondary)]"
              style={{ left: `${meter.thresholdPct}%` }}
            />
          </div>
          <div
            className="mono mt-1 text-right text-[10px]"
            style={{ color: meter.calls ? tone : 'var(--ink-tertiary)' }}
          >
            {meter.label}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}

function SplitBar({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-10 text-[10px] text-[var(--ink-tertiary)]">{label}</span>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--surface-3)]">
        <i className="block h-full rounded-full" style={{ width: `${Math.round(value)}%`, background: color }} />
      </div>
      <span className="mono w-8 text-right text-[10px] text-[var(--ink-secondary)]">{Math.round(value)}%</span>
    </div>
  );
}
